import { itemIdOf, skillOf } from "@aybuben/core/content/types.ts";
import { exampleWord, type IntroStep } from "@aybuben/core/lesson.ts";
import { useEffect } from "react";

import { useCourse } from "../../content/context.tsx";
import { useStrings } from "../../i18n/index.tsx";
import { COL } from "../../theme.ts";
import { Chip, G, Stage } from "../../ui/primitives.tsx";
import { useSound, SoundButton } from "../../ui/sound.tsx";
import { SpeakerRow } from "../../ui/SpeakerRow.tsx";
import { registerView, type ExerciseViewProps } from "../view-registry.ts";

export function IntroView({ step }: ExerciseViewProps<IntroStep, string>) {
	const course = useCourse();
	// Split by kind so each component's hooks run unconditionally.
	if (course.itemById(itemIdOf(step.key)).kind === "word") return <WordIntro step={step} />;
	return <LetterIntro step={step} />;
}

function WordIntro({ step }: { step: IntroStep }) {
	const course = useCourse();
	const t = useStrings();
	const sound = useSound();
	const w = course.views.word(itemIdOf(step.key));
	const audio = course.itemById(itemIdOf(step.key)).audio;
	useEffect(() => {
		if (audio) sound.playAuto(audio);
	}, [audio, sound]);
	return (
		<div className="flex flex-col items-center text-center">
			<h2 className="self-start text-xl font-extrabold">{t("lesson.introWord")}</h2>
			<div className="mt-5 w-full text-left">
				<SpeakerRow text={[{ t: w.a }]} asset={audio} status={null} display />
			</div>
			<p className="mt-4 text-xl font-semibold">{w.m}</p>
		</div>
	);
}

function LetterIntro({ step }: { step: IntroStep }) {
	const course = useCourse();
	const t = useStrings();
	const sound = useSound();
	const caps = skillOf(step.key) === "upper";
	const L = course.views.letter(itemIdOf(step.key));
	const ex = exampleWord(step.key, course);
	// Every letter item carries audio after E, but the field stays optional —
	// a letter without one plays nothing, gracefully, and the requirement lives
	// with the listening steps, not with intro cards.
	const audio = course.itemById(itemIdOf(step.key)).audio;
	useEffect(() => {
		if (audio) sound.playAuto(audio);
	}, [audio, sound]);
	return (
		<div className="flex flex-col items-center text-center">
			<h2 className="self-start text-xl font-extrabold">
				{caps ? t("lesson.introCapital") : t("lesson.introLetter")}
			</h2>
			<div className="mt-5 w-full">
				<Stage>
					<div className="flex items-end justify-center gap-6">
						{caps ? (
							<>
								<G size={112}>{L.u}</G>
								<G size={60} style={{ color: COL.faint }}>
									{L.l}
								</G>
							</>
						) : (
							<>
								<G size={120}>{L.l}</G>
								<G size={54} style={{ color: COL.faint }}>
									{L.u || ""}
								</G>
							</>
						)}
					</div>
				</Stage>
			</div>
			<div className="mt-5">
				<Chip big>{L.r}</Chip>
			</div>
			<p className="mt-3 px-2 leading-relaxed" style={{ color: COL.mute }}>
				{L.h}
			</p>
			{ex && (
				<div
					className="mt-5 flex w-full items-center justify-between rounded-2xl px-4 py-3"
					style={{ background: COL.tint }}
				>
					<G size={28}>{ex.a}</G>
					<span className="text-right text-sm" style={{ color: COL.mute }}>
						<b style={{ color: COL.ink }}>{ex.r}</b>, {ex.m}
					</span>
				</div>
			)}
			{audio && (
				<div className="mt-4 flex justify-center">
					<SoundButton asset={audio} />
				</div>
			)}
		</div>
	);
}

// No Feedback: an intro is never graded, so the shell never shows a panel
// for it.
registerView<IntroStep, string>("intro", { View: IntroView, action: "continue" });
